import {
  GetServerSidePropsResult,
  InferGetServerSidePropsType,
  NextPage,
} from 'next';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { injectAppContextGetServerSideProps } from '../../../_app';
import MetaTags, {
  COUBIC_ORIGIN,
} from '../../../../components/layouts/MetaTags';
import MerchantResourceBookPage from '../../../../templates/merchants/resources/express/IndexTemplate';
import { fetchMerchantDetail } from '../../../../infrastructure/api';
import { MerchantDetail } from '../../../../interfaces/merchants';
import ErrorTemplate from '../../../../templates/ErrorTemplate';
import { ReservationFlowWithThrottle } from 'src/components/ReservationFlowWithThrottle';
import { BookingPageRepositoryForSSR } from '../../../../infrastructure/repositories/BookingPageRepository';
import MerchantCourseDetailsPageProtected from '../../../../molecules/merchant/resources/book/MerchantCourseDetailsPageProtected';
import { MerchantSite } from '../../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../../hooks/gtm';
import { sanitizeResourcePublicId } from '../../../../utils/sanitizeResourcePublicId';
import { MerchantBookingPageApiResponse } from '../../../../interfaces/merchants/bookings';
import axios from 'axios';
import { toThumbnailUrl } from 'src/utils/metatag';
import { getServerSideApiClient } from 'src/openapi/axiosClient';
import { getDestinationFromV1ToV2 } from 'src/utils/redirectReservationFlowVersion';

interface Query {
  publicId: string;
  resource_id: string;
  selected_date?: string;
  selected_slot?: string;
  selected_lesson?: string;
  selected_course?: string;
  selected_staff?: string;
}

interface Props {
  session: string;
  merchant?: MerchantDetail;
  token: string;
  requestId: string;
  merchantId: string;
  resourceId: string;
  selectedDate?: string;
  selectedSlot?: string;
  selectedLesson?: string;
  selectedCourse?: string;
  selectedStaff?: string;
  isUnpublishedSite: boolean;
  isAccepting: boolean;
  isPasswordProtected: boolean;
  bookingData?: MerchantBookingPageApiResponse;
  isCoubicApp: boolean;
  isRateLimited: boolean;
}

const MerchantResourceBookWithThrottle: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props: Props) => {
  return (
    <ReservationFlowWithThrottle isRateLimited={props.isRateLimited}>
      <MerchantResourceBook {...props} />
    </ReservationFlowWithThrottle>
  );
};

const MerchantResourceBook: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props: Props) => {
  const {
    session,
    token,
    requestId,
    merchantId,
    merchant,
    resourceId,
    selectedDate,
    selectedSlot,
    selectedLesson,
    selectedCourse,
    selectedStaff,
    isAccepting,
    isPasswordProtected,
    isUnpublishedSite,
    bookingData,
    isCoubicApp,
  } = props;
  const intl = useIntl();
  usePushMerchantDataLayerEffect({
    visit_merchant_id: merchant?.id,
  });

  const [passwordProtected, setPasswordProtected] =
    useState(isPasswordProtected);
  const onPasswordSubmit = (isCorrectPassword: boolean) => {
    if (isCorrectPassword) {
      setPasswordProtected(false);
    }
  };

  return (
    <MerchantSite
      isProtected={passwordProtected}
      protectedElement={
        <MerchantCourseDetailsPageProtected
          publicId={merchantId}
          servicePublicId={resourceId}
          session={session}
          token={token}
          requestId={requestId}
          onSubmit={onPasswordSubmit}
        />
      }
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchant}
      logoImage={merchant?.logo_image || null}
      merchantPublicId={merchant?.public_id || null}
      displayName={merchant?.display_name || null}
      isCoubicApp={isCoubicApp}
    >
      {merchant ? (
        <>
          <MetaTags
            title={intl.formatMessage({ id: 'books.meta.title' })}
            currentUrl={`${COUBIC_ORIGIN}/${merchantId}/${resourceId}`}
            noIndex={merchant.seo_setting?.no_index}
            thumbnailUrl={
              bookingData
                ? toThumbnailUrl({
                    merchantDetail: merchant,
                    bookingData: bookingData,
                  })
                : undefined
            }
            googleAnalyticsFourTrackingId={
              merchant.available_google_analytics_four_tracking_id
            }
            faviconImageUrl={merchant?.favicon_image?.url}
          />
          <MerchantResourceBookPage
            merchantId={merchantId}
            resourceId={resourceId}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            selectedLesson={selectedLesson}
            selectedCourse={selectedCourse}
            selectedStaff={selectedStaff}
            isAccepting={isAccepting}
            lineLiffId={merchant.line_liff_id}
            isCoubicApp={isCoubicApp}
          />
        </>
      ) : (
        <ErrorTemplate statusCode={404} />
      )}
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx): Promise<GetServerSidePropsResult<Props>> => {
    const { res, query, session, token, requestId, isCoubicApp } = ctx;
    const {
      publicId: merchantPublicId,
      resource_id,
      selected_date,
      selected_slot,
      selected_lesson,
      selected_course,
      selected_staff,
    } = query as unknown as Query;

    const merchantId = merchantPublicId;
    const resourceId = resource_id;
    const selectedDate = selected_date;
    const selectedSlot = selected_slot;
    const selectedLesson = selected_lesson;
    const selectedCourse = selected_course;
    const selectedStaff = selected_staff;

    const client = getServerSideApiClient(ctx);
    let isRateLimited = false;
    try {
      const { csrfToken } = await client.getCsrfToken();
      await client.postReservationFlowMerchantThrottle({
        parameter: {
          merchant_public_id: merchantPublicId,
          'X-Csrf-Token': csrfToken,
        },
      });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 429) {
        isRateLimited = true;
      } else {
        throw e;
      }
    }

    if (isRateLimited) {
      res.statusCode = 429;
      return {
        props: {
          session,
          token,
          requestId,
          merchantId,
          resourceId,
          selectedDate,
          selectedSlot,
          selectedLesson,
          selectedCourse,
          selectedStaff,
          isUnpublishedSite: false,
          isAccepting: false,
          isPasswordProtected: false,
          isCoubicApp,
          isRateLimited,
        },
      };
    }

    const { merchant, isUnpublishedSite } =
      // Layoutで取得している部分を徐々に置き換え中
      await fetchMerchantDetail({
        session,
        token,
        requestId,
        merchantPublicId,
      });

    if (isUnpublishedSite) {
      return {
        props: {
          session,
          token,
          requestId,
          merchant,
          merchantId,
          resourceId,
          selectedDate,
          selectedSlot,
          selectedLesson,
          selectedCourse,
          selectedStaff,
          isUnpublishedSite,
          isAccepting: false,
          isPasswordProtected: false,
          isCoubicApp,
          isRateLimited,
        },
      };
    }

    const repository = BookingPageRepositoryForSSR(session, token, requestId);

    const sanitizedResourcePublicId = sanitizeResourcePublicId(resource_id);

    const { bookable } = await repository.Bookable(
      merchantPublicId,
      resource_id,
    );
    const isAccepting = !!bookable?.accepting;
    const isPasswordProtected = !!bookable?.protected;

    // axios.getだとcookieが載らないので、BookingPageRepositoryForSSR経由で取得
    let bookingData: MerchantBookingPageApiResponse | undefined;

    try {
      bookingData = await repository.Find(
        merchantPublicId,
        sanitizedResourcePublicId,
      );
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        // パスワードプロテクトされたページを未ログイン状態で見た時は401なのでそれを無視する
      } else {
        throw e;
      }
    }

    const flowVersionResult = await client.getReservationFlowVersion({
      parameter: {
        merchant_public_id: merchantPublicId,
        resource_public_id: sanitizedResourcePublicId,
      },
    });

    const shouldBeRedirectedToV2 =
      flowVersionResult.reservation_flow_version === 'v2';
    // 新予約フローが有効な場合、新予約フローのexppressにリダイレクトする
    // bookingData が必要なのでここで判定する
    if (shouldBeRedirectedToV2) {
      const destination = getDestinationFromV1ToV2({
        merchant_public_id: merchantPublicId,
        resource_public_id: sanitizedResourcePublicId,
        query,
        bookingData,
        express: true,
      });

      return {
        redirect: {
          destination,
          permanent: false,
        },
      };
    }

    const props = {
      session,
      token,
      requestId,
      merchant,
      merchantId,
      resourceId,
      selectedDate,
      selectedSlot,
      selectedLesson,
      selectedCourse,
      selectedStaff,
      isUnpublishedSite,
      isAccepting,
      isPasswordProtected,
      bookingData,
      isCoubicApp,
      isRateLimited,
    };
    return { props };
  },
);

export default MerchantResourceBookWithThrottle;
